import React from 'react';
import type {
  VcfWorkstream,
  VcfCapability,
  VcfActivity,
  VcfGuardrail,
} from '@/hooks/useWorkstreams';
import type { FrameworkChangeItem } from '@/hooks/useFrameworkChanges';

// ============ Entity → Columns converters ============

export function activityToColumns(act: VcfActivity): Record<string, unknown> {
  return {
    id: act.id,
    capabilityId: act.capabilityId,
    name: act.name,
    role: act.role,
    skill: act.skill,
    skillId: act.skillId,
    baseDays: act.baseDays,
    description: act.description,
    activityPhase: act.activityPhase,
    isMilestone: act.isMilestone ? 1 : 0,
    customerDays: act.customerDays,
    customerRole: act.customerRole,
    sortOrder: act.sortOrder,
  };
}

export function capabilityToColumns(cap: VcfCapability): Record<string, unknown> {
  return {
    id: cap.id,
    workstreamId: cap.workstreamId,
    code: cap.code,
    name: cap.name,
    description: cap.description,
    cls: cap.cls,
    defaultIncluded: cap.defaultIncluded ? 1 : 0,
    sortOrder: cap.sortOrder,
  };
}

export function workstreamToColumns(ws: VcfWorkstream): Record<string, unknown> {
  return {
    id: ws.id,
    code: ws.code,
    name: ws.name,
    description: ws.description,
    phase: ws.phase,
    pillar: ws.pillar,
    category: ws.category,
    sortOrder: ws.sortOrder,
  };
}

export function guardrailToColumns(gr: VcfGuardrail): Record<string, unknown> {
  return {
    id: gr.id,
    capabilityId: gr.capabilityId,
    name: gr.name,
    code: gr.code,
    baseline: gr.baseline,
    multiplier: gr.multiplier,
    baselineValue: gr.baselineValue,
    multiplierFactor: gr.multiplierFactor,
    multiplierStep: gr.multiplierStep,
    multiplierLabel: gr.multiplierLabel,
    optionFactors: gr.optionFactors ? JSON.stringify(gr.optionFactors) : null,
    inputType: gr.inputType,
    options: gr.options ? JSON.stringify(gr.options) : null,
    defaultValue: gr.defaultValue,
    minValue: gr.minValue,
    maxValue: gr.maxValue,
    sortOrder: gr.sortOrder,
  };
}

// ============ Pending item → Entity converters ============

export function pendingItemToActivity(item: FrameworkChangeItem): VcfActivity | null {
  if (!item.afterJson) return null;
  try {
    const a = JSON.parse(item.afterJson) as Record<string, unknown>;
    return {
      id: (a.id as string) || item.entityId || '',
      capabilityId: (a.capabilityId as string) || '',
      name: (a.name as string) || '',
      description: (a.description as string | null) ?? null,
      role: (a.role as string) || '',
      skill: (a.skill as string | null) ?? null,
      skillId: (a.skillId as string | null) ?? null,
      baseDays: (a.baseDays as number) || 0,
      activityPhase: (a.activityPhase as VcfActivity['activityPhase']) ?? null,
      isMilestone: Boolean(a.isMilestone),
      sortOrder: (a.sortOrder as number) || 0,
      isActive: true,
      customerDays: (a.customerDays as number) || 0,
      customerRole: (a.customerRole as string | null) ?? null,
      dependencies: [],
    };
  } catch {
    return null;
  }
}

export function pendingItemToCapability(item: FrameworkChangeItem): VcfCapability | null {
  if (!item.afterJson) return null;
  try {
    const c = JSON.parse(item.afterJson) as Record<string, unknown>;
    return {
      id: (c.id as string) || item.entityId || '',
      workstreamId: (c.workstreamId as string) || '',
      code: (c.code as string) || '',
      name: (c.name as string) || '',
      description: (c.description as string | null) ?? null,
      cls: (c.cls as VcfCapability['cls']) ?? null,
      defaultIncluded: Boolean(c.defaultIncluded),
      sortOrder: (c.sortOrder as number) || 0,
      isActive: true,
      scopeText: (c.scopeText as string | null) ?? null,
      deliverables: (c.deliverables as string[] | null) ?? null,
      prerequisites: (c.prerequisites as string[] | null) ?? null,
      activities: [],
      guardrails: [],
    };
  } catch {
    return null;
  }
}

export function pendingItemToWorkstream(item: FrameworkChangeItem): VcfWorkstream | null {
  if (!item.afterJson) return null;
  try {
    const w = JSON.parse(item.afterJson) as Record<string, unknown>;
    return {
      id: (w.id as string) || item.entityId || '',
      code: (w.code as string) || '',
      name: (w.name as string) || '',
      description: (w.description as string | null) ?? null,
      product: null,
      phase: (w.phase as VcfWorkstream['phase']) ?? null,
      pillar: (w.pillar as VcfWorkstream['pillar']) ?? null,
      category: (w.category as string | null) ?? null,
      sortOrder: (w.sortOrder as number) || 0,
      isActive: true,
      createdAt: '',
      updatedAt: '',
      capabilities: [],
    };
  } catch {
    return null;
  }
}

export function pendingItemToGuardrail(item: FrameworkChangeItem): VcfGuardrail | null {
  if (!item.afterJson) return null;
  try {
    const g = JSON.parse(item.afterJson) as Record<string, unknown>;
    return {
      id: (g.id as string) || item.entityId || '',
      capabilityId: (g.capabilityId as string) || '',
      name: (g.name as string) || '',
      code: (g.code as string) || '',
      baseline: (g.baseline as string | null) ?? null,
      multiplier: (g.multiplier as string | null) ?? null,
      baselineValue: (g.baselineValue as number | null) ?? null,
      multiplierFactor: (g.multiplierFactor as number | null) ?? null,
      multiplierStep: (g.multiplierStep as number | null) ?? null,
      multiplierLabel: (g.multiplierLabel as string | null) ?? null,
      optionFactors: g.optionFactors ? JSON.parse(g.optionFactors as string) : null,
      inputType: (g.inputType as 'number' | 'select' | 'boolean') || 'number',
      options: g.options ? JSON.parse(g.options as string) : null,
      defaultValue: (g.defaultValue as string | null) ?? null,
      minValue: (g.minValue as number | null) ?? null,
      maxValue: (g.maxValue as number | null) ?? null,
      sortOrder: (g.sortOrder as number) || 0,
    };
  } catch {
    return null;
  }
}

// ============ Pending update merger ============

export function applyPendingUpdate<T>(entity: T, item: FrameworkChangeItem | undefined): T {
  if (!item || item.changeType !== 'update' || !item.afterJson) return entity;
  try {
    const after = JSON.parse(item.afterJson) as Record<string, unknown>;
    return { ...(entity as object), ...after } as T;
  } catch {
    return entity;
  }
}

// ============ Merged workstreams hook ============

export function useMemoMergedWorkstreams(
  liveWorkstreams: VcfWorkstream[],
  pendingCreatedWorkstreams: FrameworkChangeItem[],
  pendingCreatedCapabilities: FrameworkChangeItem[],
  pendingCreatedActivities: FrameworkChangeItem[],
  pendingCreatedGuardrails: FrameworkChangeItem[],
  pendingWsMap: Map<string, FrameworkChangeItem>,
  pendingCapMap: Map<string, FrameworkChangeItem>,
  pendingActMap: Map<string, FrameworkChangeItem>,
  pendingGrdMap: Map<string, FrameworkChangeItem>,
): VcfWorkstream[] {
  return React.useMemo(() => {
    // Pending creates → bring into existence as virtual entities
    const virtualWorkstreams = pendingCreatedWorkstreams
      .map(pendingItemToWorkstream)
      .filter((w): w is VcfWorkstream => w !== null);
    const virtualCapsByWsId = new Map<string, VcfCapability[]>();
    for (const item of pendingCreatedCapabilities) {
      const cap = pendingItemToCapability(item);
      if (!cap) continue;
      const list = virtualCapsByWsId.get(cap.workstreamId) || [];
      list.push(cap);
      virtualCapsByWsId.set(cap.workstreamId, list);
    }
    const virtualActsByCapId = new Map<string, VcfActivity[]>();
    for (const item of pendingCreatedActivities) {
      const act = pendingItemToActivity(item);
      if (!act) continue;
      const list = virtualActsByCapId.get(act.capabilityId) || [];
      list.push(act);
      virtualActsByCapId.set(act.capabilityId, list);
    }
    const virtualGrsByCapId = new Map<string, VcfGuardrail[]>();
    for (const item of pendingCreatedGuardrails) {
      const gr = pendingItemToGuardrail(item);
      if (!gr) continue;
      const list = virtualGrsByCapId.get(gr.capabilityId) || [];
      list.push(gr);
      virtualGrsByCapId.set(gr.capabilityId, list);
    }

    // Walk the live tree, applying pending updates and attaching pending creates
    const merged: VcfWorkstream[] = liveWorkstreams.map((ws) => {
      const wsItem = pendingWsMap.get(ws.id);
      const wsMerged = applyPendingUpdate(ws, wsItem);

      const liveCaps = ws.capabilities || [];
      const mergedCaps: VcfCapability[] = liveCaps.map((cap) => {
        const capItem = pendingCapMap.get(cap.id);
        const capMerged = applyPendingUpdate(cap, capItem);

        const liveActs = cap.activities || [];
        const mergedActs: VcfActivity[] = liveActs.map((act) => {
          const actItem = pendingActMap.get(act.id);
          return applyPendingUpdate(act, actItem);
        });
        // Append virtual activities for this capability
        const virtAdds = virtualActsByCapId.get(cap.id) || [];
        const mergedActsWithVirtual = [...mergedActs, ...virtAdds];

        const liveGuards = cap.guardrails || [];
        const mergedGuards: VcfGuardrail[] = liveGuards.map((g) => {
          const grItem = pendingGrdMap.get(g.id);
          return applyPendingUpdate(g, grItem);
        });
        const virtGuardAdds = virtualGrsByCapId.get(cap.id) || [];
        const mergedGuardsWithVirtual = [...mergedGuards, ...virtGuardAdds];

        return {
          ...capMerged,
          activities: mergedActsWithVirtual,
          guardrails: mergedGuardsWithVirtual,
        };
      });

      const virtCapAdds = virtualCapsByWsId.get(ws.id) || [];
      // Pending-created capabilities also receive their pending-created activities/guardrails
      const enrichedVirtCaps = virtCapAdds.map((cap) => ({
        ...cap,
        activities: virtualActsByCapId.get(cap.id) || [],
        guardrails: virtualGrsByCapId.get(cap.id) || [],
      }));

      return {
        ...wsMerged,
        capabilities: [...mergedCaps, ...enrichedVirtCaps],
      };
    });

    // Append virtual workstreams (pending creates that don't exist live yet)
    for (const vws of virtualWorkstreams) {
      const enrichedCaps = (virtualCapsByWsId.get(vws.id) || []).map((cap) => ({
        ...cap,
        activities: virtualActsByCapId.get(cap.id) || [],
        guardrails: virtualGrsByCapId.get(cap.id) || [],
      }));
      merged.push({ ...vws, capabilities: enrichedCaps });
    }

    return merged;
  }, [
    liveWorkstreams,
    pendingCreatedWorkstreams,
    pendingCreatedCapabilities,
    pendingCreatedActivities,
    pendingCreatedGuardrails,
    pendingWsMap,
    pendingCapMap,
    pendingActMap,
    pendingGrdMap,
  ]);
}
